import React, { useEffect, useState, useRef } from "react";
import avatar from "../data/avatar6.png";
import visionIcon from "../assets/images/vision_icon.png";

import { useLocation, useNavigate } from "react-router-dom";
import { useStateContext } from "../contexts/ContextProvider";
import { IconButton, Button  } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AnimatePresence, motion } from "framer-motion";
import BitqueryLogo from "./BitqueryLogo"; // Import the new component
import ProfileDropdown from './ProfileDropdown';


// **Page Title Mapping**
const pageHeadlines = {
  "/Structure%20info": "Holders Structure Info",
  "/Price action": "Price Action Breakdown",
  "/holders-chat": "Holders Chat",
  "/Settings": "General Settings",
  "/About": "About",
  "/Login": "Login screen",
  "/SceneBuilder": "Scene Builder"
};

const Navbar = () => {
  const {
    activeMenu,
    setActiveMenu,
    screenSize,
    setScreenSize,
    isLogged,
    profileVisible,
    setProfileVisible
  } = useStateContext();
  const location = useLocation(); // Get current path

  const profileRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();


  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize]);

  const pageTitle = pageHeadlines[location.pathname] || "";

  const handleCloseDropdown = () => {
    setProfileVisible(false);
  };

  return (
    <div className="flex justify-between p-1 md:mx-6 items-center">
      {/* Left Side - Menu Button */}
      <IconButton
        size="large"
        edge="start"
        color="inherit"
        aria-label="menu"
        className="p-5 pb-2"
        onClick={() => setActiveMenu((prevActiveMenu) => !prevActiveMenu)}
      >
        {activeMenu ? (
          <motion.div
            initial={{ x: 50 }}
            animate={{ x: activeMenu ? 0 : 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 27 }}
          >
            <KeyboardDoubleArrowLeftIcon
              fontSize="large"
              style={{ fill: "url(#gradient)" }}
            />
          </motion.div>
        ) : (
          <MenuIcon style={{ fill: "url(#gradient)" }} />
        )}
        <svg width="0" height="0">
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop
                offset="0%"
                style={{ stopColor: "#ec4899", stopOpacity: 1 }}
              />
              <stop
                offset="50%"
                style={{ stopColor: "#10b981", stopOpacity: 1 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: "#3b82f6", stopOpacity: 1 }}
              />
            </linearGradient>
          </defs>
        </svg>
      </IconButton>

      {/* **Dynamic Page Title** */}
      <AnimatePresence mode="wait">
        <motion.h1
          key={pageTitle}
          className="absolute left-1/2 bold top-[5px] transform -translate-x-1/2 text-2xl opacity-30 text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-green-500 to-green-500 barlowLogo tracking-wide z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {pageTitle}
        </motion.h1>
      </AnimatePresence>

      {/* Right Side */}
      <div className="flex items-center gap-6">
        {/* Powered by Bitquery Component */}
        <BitqueryLogo />

        {/* Conditional Renddering: Login Button or User Avatar */}
        {isLogged ? (
          // Show User Avatar & Dropdown if Logged In
          <div
            className="flex items-center gap-2 cursor-pointer p-2 sm:hover:bg-light-gray rounded-lg ml-2"
            onClick={(e) => {
              e.stopPropagation();
              setProfileVisible((prev) => !prev);
            }}
          >
            <img src={avatar} className="rounded-full w-8 h-8" />
            <p>
              <span className="text-gray-400 font-semibold ml-1 text-14 select-none">User</span>
            </p>
            <KeyboardArrowDownIcon className="text-gray-400 text-12" />
          </div>
        ) : (
          // Show Login Button if Not Logged In
          <Button
            variant="outlined"
            onClick={() => navigate("/Login")}
            sx={{
              borderColor: "gray",
              color: "gray",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "rgba(107, 114, 128, 0.1)", // Light gray hover effect
              },
            }}
            className="border-gray-500 text-gray-500 px-4 pt-2 rounded-lg"
          >
            Login
          </Button>
        )}
        
        {/* Profile Dropdown */}
        <AnimatePresence>
          {isLogged && profileVisible && (
            <ProfileDropdown onClose={() => setProfileVisible(false)} dropdownRef={dropdownRef} profileRef={profileRef} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Navbar;
